import { scaleLinear, scaleOrdinal } from '@visx/scale';
import Arc from '../Arc';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import Tooltip from '@/components/Tooltip';
import {
  max,
  gaugeRadius,
  getRingRadius,
  getEndAngle,
} from '@/components/Gauge/utils';
import Axis from '../Axis';
import { Label } from '../Label';
import TooltipTable from '../TooltipTable';
import type { PinSize } from '../Pin';
import Pin from '../Pin';
import { useTheme } from '@simplywallst/ui-core';
import { GaugeWrapper } from './styled';

export interface Props<Key extends string, E extends Key> {
  entities: Record<E, number | null>;
  entityTitles?: Record<E, string>;
  flip?: boolean;
  formatter: (value: number | null, significance?: number) => string;
  from: number;
  keys: Key[];
  /**
   * A set of labels to display for each key.
   * If not provided the keys themselves will be displayed directly.
   *
   * Added as a backward-compatible way to support localised labels.
   */
  keyLabels?: Record<E, string>;
  numTicks: number;
  title: string;
  to: number;
}

const height = 230;
const pins: PinSize[] = ['large', 'medium', 'small'];

const Gauge = <Key extends string>({
  entities,
  entityTitles,
  flip,
  formatter,
  from,
  keys,
  keyLabels,
  numTicks,
  title,
  to,
}: Props<Key, Key>) => {
  const theme = useTheme();
  const { gaugeBody } = useChartDefinitionsContext();

  const keyColors: string[] = [
    theme.color.chart01,
    theme.color.chart02,
    theme.color.chart03,
  ];

  const xScale = scaleLinear({
    domain: [from, to],
    range: [-max, max],
  });

  const color = scaleOrdinal<string, string>({
    domain: keys,
    range: keyColors,
  });

  const size = scaleOrdinal({
    domain: keys,
    range: pins,
  });

  return (
    <GaugeWrapper>
      <svg width="100%" height={height} role="document" aria-label={title}>
        <g transform="translate(150 150)">
          <use
            xlinkHref={`#${gaugeBody}`}
            transform={`scale(${flip === true ? -1 : 1} 1)`}
          />
          {keys.map((key, i) => {
            const value = entities[key];
            return value !== null ? (
              <Arc
                key={`gauge-arc-${key}-${value}`}
                outerRadius={getRingRadius('outer', i + 1)}
                innerRadius={getRingRadius('inner', i + 1)}
                startAngle={xScale(from)}
                endAngle={getEndAngle(xScale, value)}
                fill={color(key)}
                fillOpacity={0.1}
              />
            ) : null;
          })}
          <circle cx="0" cy="0" r="12" fill="white" fillOpacity="0.2" />
          {keys
            .filter((key) => entities[key] !== null)
            .map((key) => {
              const value = entities[key];
              return value !== null ? (
                <Pin
                  key={`gauge-pin-${key}-${value}`}
                  size={size(key)}
                  angle={getEndAngle(xScale, value)}
                  fill={color(key)}
                />
              ) : null;
            })}
          <Axis
            xScale={xScale}
            numTicks={numTicks}
            formatter={formatter}
            radius={gaugeRadius}
            offset={14}
          />
        </g>
        {entityTitles && (
          <Tooltip
            offsetX={0}
            offsetY={-128}
            placement="top"
            title={
              <TooltipTable
                title={title}
                keys={keys}
                entities={entities}
                entityTitles={entityTitles}
                color={color}
                formatter={formatter}
              />
            }
          >
            <rect width="100%" height="100%" fill="transparent" />
          </Tooltip>
        )}
      </svg>
      <Label
        title={title}
        keys={keys}
        keyLabels={keyLabels}
        entities={entities}
        color={color}
        formatter={formatter}
      />
    </GaugeWrapper>
  );
};

export default Gauge;
